/**
 * Projects API Client
 */

import type { Project, CreateProjectDto, UpdateProjectDto } from '@fmeastudio/shared'
import { API_BASE_URL } from './config'

const BASE_URL = `${API_BASE_URL}/projects`

/**
 * Thrown by `getProjects` on a non-OK response. Carries the HTTP status and,
 * for 429s, how long the caller should wait before retrying — parsed from
 * the `Retry-After` header first, falling back to the `retryAfter` field the
 * backend rate limiter puts in its JSON body (see middleware/rateLimit.ts).
 * ProjectContext uses both to drive its bounded backoff instead of guessing.
 */
export class ProjectsFetchError extends Error {
  status?: number
  retryAfterSeconds?: number

  constructor(message: string, status?: number, retryAfterSeconds?: number) {
    super(message)
    this.name = 'ProjectsFetchError'
    this.status = status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function parseRetryAfterSeconds(response: Response, body: unknown): number | undefined {
  const header = response.headers.get('Retry-After')
  if (header) {
    const seconds = Number(header)
    if (!Number.isNaN(seconds)) return seconds
  }
  const bodyRetryAfter = (body as { retryAfter?: unknown } | null)?.retryAfter
  if (typeof bodyRetryAfter === 'number') return bodyRetryAfter
  return undefined
}

export async function getProjects(includeArchived = false): Promise<Project[]> {
  const url = includeArchived
    ? `${BASE_URL}?includeArchived=true`
    : BASE_URL

  // credentials: 'include' — required whenever frontend and backend are on
  // different origins (e.g. local dev on separate ports); same-origin
  // deployments send the cookie regardless, so this is a no-op there.
  const response = await fetch(url, { credentials: 'include' })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    const message =
      (body as { error?: string } | null)?.error ||
      `Failed to fetch projects (${response.status})`
    throw new ProjectsFetchError(message, response.status, parseRetryAfterSeconds(response, body))
  }
  const data = await response.json()
  return data.data || []
}

export async function getProject(id: string): Promise<Project> {
  const response = await fetch(`${BASE_URL}/${id}`)
  if (!response.ok) {
    throw new Error('Failed to fetch project')
  }
  const data = await response.json()
  return data.data
}

export async function createProject(data: CreateProjectDto): Promise<Project> {
  const response = await fetch(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    throw new Error('Failed to create project')
  }
  const result = await response.json()
  return result.data
}

export async function updateProject(
  id: string,
  data: UpdateProjectDto
): Promise<Project> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    throw new Error('Failed to update project')
  }
  const result = await response.json()
  return result.data
}

export async function archiveProject(id: string): Promise<Project> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'ARCHIVED' }),
  })
  if (!response.ok) {
    throw new Error('Failed to archive project')
  }
  const result = await response.json()
  return result.data
}

export async function deleteProject(id: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/${id}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error('Failed to delete project')
  }
}

export async function duplicateProject(
  id: string,
  name: string
): Promise<Project> {
  const response = await fetch(`${BASE_URL}/${id}/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  })
  if (!response.ok) {
    throw new Error('Failed to duplicate project')
  }
  const result = await response.json()
  return result.data
}

/**
 * FEA-034: Diagram Live Refresh
 * Get the diagram version (latest modification timestamp)
 */
export interface DiagramVersion {
  version: number
  lastModified: string
}

export async function getDiagramVersion(projectId: string): Promise<DiagramVersion> {
  const response = await fetch(`${BASE_URL}/${projectId}/diagram-version`)
  if (!response.ok) {
    throw new Error('Failed to fetch diagram version')
  }
  const result = await response.json()
  return result.data
}

/**
 * FEA-034: Diagram Live Refresh
 * Trigger a diagram refresh (for chatbot use)
 */
export async function triggerDiagramRefresh(projectId: string): Promise<DiagramVersion> {
  const response = await fetch(`${BASE_URL}/${projectId}/trigger-refresh`, {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error('Failed to trigger diagram refresh')
  }
  const result = await response.json()
  return result.data
}

/**
 * FEA-060: Project Mission Page
 * Mission data interface
 */
export interface ProjectMission {
  missionStatement: string | null
  missionUpdatedAt: string | null
}

/**
 * FEA-060: Project Mission Page
 * Get project mission statement
 */
export async function getMission(projectId: string): Promise<ProjectMission> {
  const response = await fetch(`${BASE_URL}/${projectId}/mission`)
  if (!response.ok) {
    throw new Error('Failed to fetch project mission')
  }
  const result = await response.json()
  return result.data
}

/**
 * FEA-060: Project Mission Page
 * Update project mission statement
 */
export async function updateMission(
  projectId: string,
  missionStatement: string | null
): Promise<ProjectMission> {
  const response = await fetch(`${BASE_URL}/${projectId}/mission`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ missionStatement }),
  })
  if (!response.ok) {
    throw new Error('Failed to update project mission')
  }
  const result = await response.json()
  return result.data
}

// ============================================================================
// Template & Stats Functions (BUG-058)
// ============================================================================

/**
 * Get projects with TEMPLATE status
 */
export async function getTemplateProjects(): Promise<Project[]> {
  // TODO(F8): status filter stripped by backend — listQuerySchema drops the `status=TEMPLATE`
  // query param, so this returns ALL projects, not just templates.
  const response = await fetch(`${BASE_URL}?status=TEMPLATE`)
  if (!response.ok) {
    throw new Error('Failed to fetch templates')
  }
  // BE (projects.routes.ts:39-43) wraps { success, data: Project[], total }.
  const body = await response.json()
  return body.data || []
}

export interface ProjectStats {
  componentCount: number
  failureModeCount: number
  causeCount: number
}

/**
 * Get project statistics
 */
export async function getProjectStats(projectId: string): Promise<ProjectStats> {
  const response = await fetch(`${BASE_URL}/${projectId}/stats`)
  if (!response.ok) {
    throw new Error('Failed to fetch project stats')
  }
  // BE (projects.routes.ts:180-183) wraps { success, data: ProjectStats }.
  const body = await response.json()
  return body.data
}
